import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBars, faSignOutAlt, faMoon, faSun } from '@fortawesome/free-solid-svg-icons';
import PlatformSwitcher from './PlatformSwitcher';
import './Header.css';

// Get initials from user name (max 2 chars)
const getInitials = (user) => {
  const first = user?.firstname || user?.nom || '';
  const last = user?.lastname || user?.prenom || '';
  if (first && last) return (first[0] + last[0]).toUpperCase();
  const name = user?.name || first || 'A';
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return name.substring(0, 2).toUpperCase();
};

const Header = ({ user, onLogout, onToggleSidebar, sidebarOpen }) => {
  const [darkMode, setDarkMode] = useState(() => {
    // Initialize from localStorage immediately
    return localStorage.getItem('theme') === 'dark';
  });

  // Apply theme function - can be called anytime
  const applyTheme = (isDark) => {
    const theme = isDark ? 'dark' : 'light';
    document.documentElement.setAttribute('data-theme', theme);
    document.documentElement.classList.toggle('dark-mode', isDark);
    document.body.setAttribute('data-theme', theme);
    document.body.classList.toggle('dark-mode', isDark);
  };

  // Initial theme application
  useEffect(() => {
    const savedTheme = localStorage.getItem('theme') || 'light';
    const isDark = savedTheme === 'dark';
    setDarkMode(isDark);
    applyTheme(isDark);
  }, []);

  // Re-apply theme whenever darkMode state changes
  useEffect(() => {
    applyTheme(darkMode);
  }, [darkMode]);

  // Periodic check to ensure dark mode stays applied (protection against React re-renders)
  useEffect(() => {
    if (!darkMode) return;
    
    const intervalId = setInterval(() => {
      if (!document.body.classList.contains('dark-mode')) {
        applyTheme(true);
      }
    }, 1000); // Check every second

    return () => clearInterval(intervalId);
  }, [darkMode]);

  const toggleTheme = () => {
    const newDarkMode = !darkMode;
    setDarkMode(newDarkMode);
    applyTheme(newDarkMode);
    localStorage.setItem('theme', newDarkMode ? 'dark' : 'light');
  };

  return (
    <header className="header">
      <div className="header-left">
        <button className="menu-toggle" onClick={onToggleSidebar}>
          <FontAwesomeIcon icon={faBars} />
        </button>
        <PlatformSwitcher />
      </div>
      <div className="header-right">
        <button className="theme-toggle" onClick={toggleTheme} title={darkMode ? "Mode clair" : "Mode sombre"}>
          <FontAwesomeIcon icon={darkMode ? faSun : faMoon} />
        </button>
        <div className="user-info">
          <span className="header-avatar">{getInitials(user)}</span>
          <span className="user-name">
            {user?.firstname || user?.nom || user?.name || 'Admin'} {user?.lastname || user?.prenom || ''}
          </span>
        </div>
        <button className="logout-button" onClick={onLogout}>
          <FontAwesomeIcon icon={faSignOutAlt} />
          <span>Déconnexion</span>
        </button>
      </div>
    </header>
  );
};

export default Header;
